import { defineStore } from 'pinia'
import { computed, ref, toRaw, watch } from 'vue'
import useVuelidate from '@vuelidate/core'
import { isEqual } from 'lodash'
import { helpers } from '@vuelidate/validators'
import {
  CustomFieldType,
  CustomFieldTypeValueInput,
  FieldableFormCustomFieldFragment,
} from '@/gql/graphql'
import { isCustomFieldRequired } from '@/components/Fields/utils/CustomFieldUtils'
import { $t } from '@/composables/translation'

export const useFieldableFormStore = defineStore(`fieldableFormStore`, () => {
  const fields = ref<FieldableFormCustomFieldFragment[]>([])
  const fieldValues = ref<CustomFieldTypeValueInput[]>([])
  const persistedFieldValues = ref<CustomFieldTypeValueInput[]>([])
  const formIsDirty = ref<boolean>(false)

  watch(fieldValues, (newFieldValues) => {
    const compareTo = newFieldValues.map((newFieldValue) => toRaw(newFieldValue))
    const compareFrom = persistedFieldValues.value.map((persistedFieldValue) =>
      toRaw(persistedFieldValue),
    )

    formIsDirty.value = !isEqual(compareFrom, compareTo)
  })

  // Formatting custom field values as key being the field id and value being the
  // reactive object containing the value. This format works nicely with vuelidate
  const fieldKeyValues = computed(() =>
    fields.value.reduce((accumulator, { id }) => {
      const value = fieldValues.value?.find((fieldValue) =>
        [
          fieldValue.singleSelect?.id,
          fieldValue.freeText?.id,
          fieldValue.multiSelect?.id,
          fieldValue.date?.id,
          fieldValue.boolean?.id,
        ].includes(id),
      )

      return {
        ...accumulator,
        [id]: value,
      }
    }, {}),
  )

  const requiredField = (value, field) => {
    if (field.fieldType === CustomFieldType.FreeText && !value?.freeText.value) {
      return false
    }
    if (field.fieldType === CustomFieldType.SingleSelect && !value?.singleSelect.value) {
      return false
    }
    if (field.fieldType === CustomFieldType.MultiSelect && !value?.multiSelect?.values?.length) {
      return false
    }
    if (field.fieldType === CustomFieldType.Date && !value?.date?.value) {
      return false
    }
    // A required yes/no is answered by false as much as by true.
    if (
      field.fieldType === CustomFieldType.Boolean &&
      (value?.boolean?.value === null || value?.boolean?.value === undefined)
    ) {
      return false
    }
    return true
  }

  // Formatting rules to match the fieldKeyValues custom field structure
  const rules = computed(() =>
    Object.keys(fieldKeyValues.value).reduce((accumulator, key) => {
      const field = fields.value.find((field1) => field1.id === key)
      if (field && isCustomFieldRequired(field.settings)) {
        return {
          ...accumulator,
          [key]: {
            required:
              field &&
              helpers.withMessage(`${field.title} is required`, (value) =>
                requiredField(value, field),
              ),
          },
        }
      }
      return accumulator
    }, {}),
  )

  const v$ = useVuelidate(rules, fieldKeyValues, { $autoDirty: false })

  const errors = computed(() =>
    v$.value.$errors.reduce(
      (accumulator, error) => ({
        ...accumulator,
        [error.$property]: ref(error.$message),
      }),
      {},
    ),
  )

  const updateFields = (newFields: FieldableFormCustomFieldFragment[]) => {
    fields.value = newFields
  }

  // Important to use this method for formIsDirty to work
  const loadPersistedFieldValues = (newPersistedFieldValues: CustomFieldTypeValueInput[]) => {
    persistedFieldValues.value = newPersistedFieldValues
  }

  const updateFieldValues = (newFieldValues: CustomFieldTypeValueInput[]) => {
    fieldValues.value = newFieldValues
  }

  const selectedValues = computed(() => {
    // Create a lookup map for fields by ID to avoid repetitive searches
    const fieldMap = new Map(fields.value.map((field) => [field.id, field]))

    return fieldValues.value.map((fieldValue) => {
      const fieldId =
        fieldValue.singleSelect?.id ||
        fieldValue.freeText?.id ||
        fieldValue.multiSelect?.id ||
        fieldValue.date?.id ||
        fieldValue.boolean?.id
      if (!fieldId) return ''
      const field = fieldMap.get(fieldId)

      if (!field) return '' // Handle case where field is not found

      if (field.fieldType === CustomFieldType.MultiSelect) {
        const values = fieldValue.multiSelect?.values ?? []
        return values
          .map((valueId) => field.customFieldOptions.find((option) => option.id === valueId)?.value)
          .filter(Boolean)
          .join(', ')
      }

      const fieldValueObject = Object.values(fieldValue)[0]

      if (fieldValueObject && 'value' in fieldValueObject) {
        // Determine the value based on the field type
        if (field.fieldType === CustomFieldType.FreeText || field.fieldType === CustomFieldType.Date) {
          return fieldValueObject.value || ''
        }

        if (field.fieldType === CustomFieldType.Boolean) {
          if (fieldValueObject.value === null || fieldValueObject.value === undefined) {
            return ''
          }

          return fieldValueObject.value ? $t('Yes') : $t('No')
        }

        if (field.fieldType === CustomFieldType.SingleSelect) {
          return (
            field.customFieldOptions.find((option) => option.id === fieldValueObject.value)
              ?.value || ''
          )
        }
      }
      return '' // Handle any unexpected field types
    })
  })

  return {
    // State
    fields,
    fieldValues,
    persistedFieldValues,
    v$,
    errors,
    rules,
    formIsDirty,
    selectedValues,
    // Actions
    updateFields,
    updateFieldValues,
    loadPersistedFieldValues,
  }
})
